import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Table, Button, Form, Input, Space, Row, Col, Select, Tag, Tooltip, message, Modal, Alert, Popconfirm, Image } from "antd";
import { SearchOutlined, SyncOutlined, FilterOutlined, UpOutlined, EyeOutlined, BugOutlined, MessageOutlined, BellOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import { RootState } from '../../app/store';
import './Report.scss';

const { TextArea } = Input;

interface Report {
  _id: string;
  userId: string;
  type: 'feedback' | 'bug_report';
  title: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_review' | 'resolved' | 'closed';
  userEmail?: string;
  userInfo?: {
    platform: string;
    version: string;
    device?: string;
  };
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
  images?: string[];
  screenshotUrls?: string[];
  screenshots?: string[];
  attachments?: { type?: string; url?: string }[];
  imageUrl?: string;
  image?: string;
}

interface ReportStats {
  total: number;
  byType: { feedback: number; bug_report: number };
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
}

// Status options
const statusOptions = [
  { value: "pending", label: "Pending" },
  { value: "in_review", label: "In Review" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

// Priority options
const priorityOptions = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

// Type options
const typeOptions = [
  { value: "feedback", label: "Feedback" },
  { value: "bug_report", label: "Bug Report" },
];

export const ReportPage: React.FC = () => {
  const { token } = useSelector((state: RootState) => state.auth);
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm(); // Add separate form for modal
  const [reports, setReports] = useState<Report[]>([]);
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchVisible, setSearchVisible] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    priority: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10
  });

  useEffect(() => {
    if (token) {
      fetchReports();
      fetchStats();
    }
    // Fetch functions intentionally use the current filter state when invoked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchReports = async (page = 1, limit = 10) => {
    if (!token) {
      message.error("Authentication required");
      return;
    }

    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());
      
      // Add timestamp to prevent caching
      queryParams.append('_t', Date.now().toString());

      const baseUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3000' 
        : 'https://monea-express-api.vercel.app';
      
      console.log('🔍 [Frontend] Fetching reports with token:', token.substring(0, 20) + '...');
      console.log('🔍 [Frontend] Query params:', queryParams.toString());
      console.log('🔍 [Frontend] API URL:', `${baseUrl}/reports?${queryParams.toString()}`);

      const response = await fetch(`${baseUrl}/reports?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      console.log('📊 [Frontend] Reports API Response:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('📊 [Frontend] Reports data received:', data);
        
        if (data.success && data.data) {
          setReports(data.data.reports || []);
          setPagination({
            currentPage: page,
            totalPages: data.data.totalPages || 0,
            totalItems: data.data.total || 0,
            itemsPerPage: limit
          });
        } else {
          console.error('❌ [Frontend] Invalid response format:', data);
          message.error("Invalid response format");
        }
      } else {
        const errorText = await response.text();
        console.error('❌ [Frontend] Reports API Error:', response.status, errorText);
        message.error(`Failed to fetch reports: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('❌ [Frontend] Error fetching reports:', error);
      message.error("Failed to fetch reports: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!token) {
      return;
    }

    try {
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      
      const baseUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3000' 
        : 'https://monea-express-api.vercel.app';
      
      console.log('📈 [Frontend] Fetching stats with token:', token.substring(0, 20) + '...');
      console.log('📈 [Frontend] API URL:', `${baseUrl}/reports/stats?_t=${timestamp}`);

      const response = await fetch(`${baseUrl}/reports/stats?_t=${timestamp}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      console.log('📈 [Frontend] Stats API Response:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('📈 [Frontend] Stats data received:', data);
        
        if (data.success && data.data) {
          setStats(data.data);
        } else {
          console.error('❌ [Frontend] Invalid stats response format:', data);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ [Frontend] Stats API Error:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ [Frontend] Error fetching stats:', error);
    }
  };

  const deleteReport = async (reportId: string) => {
    if (!token) {
      message.error("Authentication required");
      return;
    }

    try {
      const baseUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:3000'
        : 'https://monea-express-api.vercel.app';

      const response = await fetch(`${baseUrl}/reports/${reportId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      });

      if (response.ok) {
        const data = await response.json();
        message.success(data.message || "Report deleted successfully");
        await fetchReports(pagination.currentPage, pagination.itemsPerPage);
        await fetchStats();
        setShowModal(false);
        setSelectedReport(null);
        updateForm.resetFields();
      } else {
        const errorText = await response.text();
        message.error(`Failed to delete report: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      message.error("Failed to delete report: " + error.message);
    }
  };

  const onSearch = (values) => {
    const newFilters = {
      type: values.type || '',
      status: values.status || '',
      priority: values.priority || ''
    };
    setFilters(newFilters);
    fetchReports(1, pagination.itemsPerPage);
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({
      type: '',
      status: '',
      priority: ''
    });
    fetchReports(1, pagination.itemsPerPage);
  };

  const updateReportStatus = async (reportId: string, status: string, adminNotes?: string) => {
    if (!token) {
      message.error("Authentication required");
      return;
    }

    try {
      const baseUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3000' 
        : 'https://monea-express-api.vercel.app';

      console.log('🔄 [Frontend] Updating report:', {
        reportId,
        status,
        adminNotes,
        url: `${baseUrl}/reports/${reportId}`
      });

      const response = await fetch(`${baseUrl}/reports/${reportId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        },
        body: JSON.stringify({ 
          status, 
          adminNotes: adminNotes || '' 
        })
      });

      console.log('📊 [Frontend] Update Report Response:', response.status, response.statusText);

      if (response.ok) {
        const responseData = await response.json();
        console.log('✅ [Frontend] Update successful:', responseData);
        
        message.success("Report updated successfully");
        
        // Refresh both reports and stats
        await fetchReports(pagination.currentPage, pagination.itemsPerPage);
        await fetchStats();
        
        setShowModal(false);
        setSelectedReport(null);
        updateForm.resetFields(); // Reset form after update
      } else {
        const errorText = await response.text();
        console.error('❌ [Frontend] Update Report Error:', response.status, errorText);
        message.error(`Failed to update report: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('❌ [Frontend] Error updating report:', error);
      message.error("Failed to update report: " + error.message);
    }
  };

  const testNotification = () => {
    const pendingCount = stats?.byStatus.pending || 0;
    
    if (pendingCount > 0) {
      message.warning({
        content: `🔔 You have ${pendingCount} pending reports that need attention!`,
        duration: 5,
        style: {
          marginTop: '20vh',
          fontSize: '16px',
        },
      });
    } else {
      message.success({
        content: '✅ All reports are handled. No pending items!',
        duration: 3,
        style: {
          marginTop: '20vh',
          fontSize: '16px',
        },
      });
    }
  };

  const getPriorityTag = (priority: string) => {
    const colors = {
      low: 'green',
      medium: 'orange',
      high: 'red',
      urgent: 'purple'
    };
    return <Tag color={colors[priority as keyof typeof colors]}>{priority.toUpperCase()}</Tag>;
  };

  const getStatusTag = (status: string) => {
    const colors = {
      pending: 'orange',
      in_review: 'blue',
      resolved: 'green',
      closed: 'default'
    };
    
    // Add special styling for pending status
    const className = status === 'pending' ? 'pending-status-tag' : '';
    
    return (
      <Tag 
        color={colors[status as keyof typeof colors]} 
        className={className}
        style={status === 'pending' ? { 
          animation: 'subtlePulse 2s ease-in-out infinite',
          fontWeight: 'bold',
          textShadow: '0 1px 2px rgba(0,0,0,0.1)'
        } : {}}
      >
        {status === 'pending' && '⚠️ '}
        {status.replace('_', ' ').toUpperCase()}
      </Tag>
    );
  };

  const getTypeTag = (type: string) => {
    const colors = {
      feedback: 'blue',
      bug_report: 'red'
    };
    const icons = {
      feedback: <MessageOutlined />,
      bug_report: <BugOutlined />
    };
    return (
      <Tag color={colors[type as keyof typeof colors]} icon={icons[type as keyof typeof icons]}>
        {type.replace('_', ' ').toUpperCase()}
      </Tag>
    );
  };

  const extractImages = (report: Report) => {
    const list: string[] = [];
    const anyReport: any = report as any;
    if (Array.isArray(anyReport.images)) list.push(...anyReport.images.filter((x: any) => typeof x === 'string'));
    if (Array.isArray(anyReport.screenshotUrls)) list.push(...anyReport.screenshotUrls.filter((x: any) => typeof x === 'string'));
    if (Array.isArray(anyReport.screenshots)) list.push(...anyReport.screenshots.filter((x: any) => typeof x === 'string'));
    if (Array.isArray(anyReport.attachments)) {
      anyReport.attachments.forEach((a: any) => {
        const t = typeof a?.type === 'string' ? a.type.toLowerCase() : '';
        const u = typeof a?.url === 'string' ? a.url : '';
        if (u && (t.includes('image') || /(\.png|\.jpg|\.jpeg|\.gif|\.webp|\.bmp)$/i.test(u))) list.push(u);
      });
    }
    if (typeof anyReport.imageUrl === 'string' && anyReport.imageUrl.trim()) list.push(anyReport.imageUrl);
    if (typeof anyReport.image === 'string' && anyReport.image.trim()) list.push(anyReport.image);
    const unique = Array.from(new Set(list));
    return unique;
  };

  const paginationConfig = {
    current: pagination.currentPage,
    pageSize: pagination.itemsPerPage,
    total: pagination.totalItems,
    onChange: (page, pageSize) => {
      fetchReports(page, pageSize || pagination.itemsPerPage);
    }
  };

  const columns = [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: 50,
      render: (_, __, index) => (pagination.currentPage - 1) * pagination.itemsPerPage + index + 1
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type) => getTypeTag(type),
      filters: [
        { text: 'Feedback', value: 'feedback' },
        { text: 'Bug Report', value: 'bug_report' },
      ],
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      ellipsis: true,
      render: (title, record) => (
        <div>
          <strong>{title}</strong>
          <br />
          <small style={{ color: '#666' }}>{record.category}</small>
        </div>
      )
    },
    {
      title: "User",
      dataIndex: "userEmail",
      key: "userEmail",
      width: 180,
      render: (userEmail, record) => (
        <div>
          <div>{userEmail || 'N/A'}</div>
          {record.userInfo && (
            <small style={{ color: '#666' }}>
              {record.userInfo.platform} {record.userInfo.version}
            </small>
          )}
        </div>
      )
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (priority) => getPriorityTag(priority),
      sorter: (a, b) => {
        const priorityOrder = { low: 1, medium: 2, high: 3, urgent: 4 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => getStatusTag(status),
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'In Review', value: 'in_review' },
        { text: 'Resolved', value: 'resolved' },
        { text: 'Closed', value: 'closed' },
      ],
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (createdAt) => moment(createdAt).format("DD/MM/YYYY"),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix()
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              type="primary"
              size="small"
              onClick={() => {
                setSelectedReport(record);
                setShowModal(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this report?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => deleteReport(record._id)}
          >
            <Tooltip title="Delete">
              <Button
                icon={<DeleteOutlined />}
                danger
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Stats cards data
  const statsCards = [
    {
      title: "Total Reports",
      value: stats?.total || 0,
      icon: <SearchOutlined style={{ fontSize: '24px', color: '#1890ff' }} />,
      color: '#1890ff'
    },
    {
      title: "Feedback",
      value: stats?.byType.feedback || 0,
      icon: <MessageOutlined style={{ fontSize: '24px', color: '#52c41a' }} />,
      color: '#52c41a'
    },
    {
      title: "Bug Reports",
      value: stats?.byType.bug_report || 0,
      icon: <BugOutlined style={{ fontSize: '24px', color: '#f5222d' }} />,
      color: '#f5222d'
    },
    {
      title: "Pending",
      value: stats?.byStatus.pending || 0,
      icon: <UpOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />,
      color: '#fa8c16'
    },
  ];

  return (
    <div className="report-page">
      <Card title="Reports & Feedback Management" className="report-page-card">
        {!token && (
          <Alert
            message="Authentication Required"
            description="Please login to access reports and feedback management."
            type="warning"
            style={{ marginBottom: 24 }}
          />
        )}
        
        {/* Stats Cards */}
        {token && stats && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            {statsCards.map((stat, index) => (
              <Col span={6} key={index}>
                <Card size="small" className="stats-card">
                  <div className="stats-content">
                    <div className="stats-icon">{stat.icon}</div>
                    <div className="stats-info">
                      <div className="stats-value" style={{ color: stat.color }}>
                        {stat.value}
                      </div>
                      <div className="stats-title">{stat.title}</div>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {/* Search Container */}
        {token && (
          <div className="search-container">
            <Button
              type="default"
              icon={searchVisible ? <UpOutlined /> : <FilterOutlined />}
              onClick={() => setSearchVisible(!searchVisible)}
              style={{ marginBottom: 16 }}
            >
              {searchVisible ? "Hide Filters" : "Show Filters"}
            </Button>

            {searchVisible && (
              <Form
                form={form}
                layout="horizontal"
                onFinish={onSearch}
                className="report-search-form"
                size="small"
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="type" label="Type">
                      <Select
                        placeholder="Select report type"
                        options={typeOptions}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="status" label="Status">
                      <Select
                        placeholder="Select status"
                        options={statusOptions}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="priority" label="Priority">
                      <Select
                        placeholder="Select priority"
                        options={priorityOptions}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
                    <Space>
                      <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
                        Search
                      </Button>
                      <Button onClick={handleReset}>
                        Reset
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {token && (
          <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="default"
              icon={<BellOutlined />}
              onClick={testNotification}
              style={{ 
                background: stats?.byStatus.pending > 0 ? '#fef3cd' : '#d1ecf1',
                borderColor: stats?.byStatus.pending > 0 ? '#fdbf47' : '#bee5eb',
                color: stats?.byStatus.pending > 0 ? '#856404' : '#0c5460'
              }}
            >
              Test Notification
            </Button>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={() => {
                fetchReports(pagination.currentPage, pagination.itemsPerPage);
                fetchStats();
              }}
            >
              Refresh Data
            </Button>
          </Space>
        )}

        {/* Reports Table */}
        <Table
          columns={columns}
          dataSource={token ? reports : []}
          rowKey="_id"
          loading={loading}
          pagination={token ? paginationConfig : false}
          size="middle"
          className="report-table"
          rowClassName={(record) => record.status === 'pending' ? 'pending-row' : ''}
          locale={{
            emptyText: !token ? 'Please login to view reports' : 'No data'
          }}
        />

        {/* Report Detail Modal */}
        <Modal
          title="Report Details"
          open={showModal}
          onCancel={() => {
            setShowModal(false);
            setSelectedReport(null);
            updateForm.resetFields();
          }}
          width={800}
          footer={[
            <Button key="cancel" onClick={() => {
              setShowModal(false);
              setSelectedReport(null);
              updateForm.resetFields();
            }}>
              Close
            </Button>,
            <Button
              key="update"
              type="primary"
              onClick={() => {
                updateForm.validateFields().then((values) => {
                  if (selectedReport) {
                    console.log('🔄 [Frontend] Form values from updateForm:', values);
                    updateReportStatus(
                      selectedReport._id, 
                      values.status || selectedReport.status,
                      values.adminNotes || ''
                    );
                  }
                }).catch((error) => {
                  console.error('Form validation failed:', error);
                });
              }}
            >
              Update Report
            </Button>
          ]}
        >
          {selectedReport && (
            <div className="report-detail">
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <strong>Type:</strong> {getTypeTag(selectedReport.type)}
                </Col>
                <Col span={12}>
                  <strong>Priority:</strong> {getPriorityTag(selectedReport.priority)}
                </Col>
              </Row>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <strong>Status:</strong> {getStatusTag(selectedReport.status)}
                </Col>
                <Col span={12}>
                  <strong>Category:</strong> {selectedReport.category}
                </Col>
              </Row>

              <div style={{ marginBottom: 16 }}>
                <strong>Title:</strong>
                <p>{selectedReport.title}</p>
              </div>

              <div style={{ marginBottom: 16 }}>
                <strong>Description:</strong>
                <p style={{ whiteSpace: 'pre-wrap' }}>{selectedReport.description}</p>
              </div>

              {extractImages(selectedReport).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <strong>Images:</strong>
                  <div style={{ marginTop: 8 }}>
                    <Image.PreviewGroup>
                      {extractImages(selectedReport).map((src, idx) => (
                        <Image key={idx} src={src} width={120} height={120} style={{ objectFit: 'cover', marginRight: 8, borderRadius: 6 }} />
                      ))}
                    </Image.PreviewGroup>
                  </div>
                </div>
              )}

              {selectedReport.userInfo && (
                <div style={{ marginBottom: 16 }}>
                  <strong>User Info:</strong>
                  <p><strong>Platform:</strong> {selectedReport.userInfo.platform}</p>
                  <p><strong>Version:</strong> {selectedReport.userInfo.version}</p>
                  {selectedReport.userInfo.device && <p><strong>Device:</strong> {selectedReport.userInfo.device}</p>}
                </div>
              )}

              <Form 
                form={updateForm}
                layout="vertical"
                initialValues={{
                  status: selectedReport.status,
                  adminNotes: selectedReport.adminNotes || ''
                }}
              >
                <Form.Item 
                  name="adminNotes"
                  label={<strong>Admin Notes:</strong>}
                >
                  <TextArea
                    rows={3}
                    placeholder="Add notes for this report..."
                  />
                </Form.Item>
                <Form.Item 
                  name="status"
                  label={<strong>Update Status:</strong>}
                  rules={[{ required: true, message: 'Please select a status' }]}
                >
                  <Select
                    style={{ width: '100%' }}
                    options={statusOptions}
                    placeholder="Select new status"
                  />
                </Form.Item>
              </Form>
            </div>
          )}
        </Modal>
      </Card>
    </div>
  );
};
